/* faith-loader.js — splash gate + reveal-on-scroll observer + header scroll state. */
(function() {
  'use strict';

  // True only inside the Shopify Theme Editor preview frame.
  var inDesignMode = !!(window.Shopify && window.Shopify.designMode);

  // ── Homepage splash: fires on every load (Liquid template gates this to template.name=='index'). ──
  function initSplash() {
    // Only the FULL-SCREEN splash (not inline loaders) is animated here.
    var splash = document.querySelector('[data-faith-loader]:not([data-faith-loader-inline])');
    if (!splash) return;

    // Hold long enough for the crown to do most of a rotation, then fade out.
    var HOLD_MS = 800;
    var FADE_MS = 500;

    setTimeout(function() {
      splash.classList.add('is-hiding');
      setTimeout(function() {
        splash.classList.add('is-gone');
        flushHeroRepaint();
      }, FADE_MS);
    }, HOLD_MS);
  }

  // iOS Safari can leave the hero unpainted after the opaque full-screen splash
  // is removed — the hero is a compositor-promoted section (`isolation: isolate`
  // plus a `will-change: transform` video layer), and when the occluding splash
  // (a fixed, opaque, z-index:9999 layer) is faded out + display:none'd, WebKit
  // doesn't always recomposite what was underneath. The section stays blank
  // until the user scrolls, which forces a compositor flush. (Confirmed: the
  // splash→hero handoff in isolation is fine; the bug only appears with the full
  // homepage stack — fixed header/announcement bar over the hero.) So when the
  // splash lifts we reproduce that scroll programmatically — 1px and back, on
  // the next frame so iOS registers it. Imperceptible, and a no-op on engines
  // without the bug. Only runs on the homepage, where the splash exists.
  function flushHeroRepaint() {
    if (!document.querySelector('.faith-hero-gd')) return;
    var y = window.pageYOffset || document.documentElement.scrollTop || 0;
    window.scrollTo(0, y + 1);
    requestAnimationFrame(function() { window.scrollTo(0, y); });
  }

  // ── Reveal-on-scroll for `.faith-reveal` elements ──
  // `.faith-reveal` starts at opacity:0 and is only made visible by the
  // `is-visible` class this observer adds. That means ANY .faith-reveal markup
  // that enters the DOM after this ran, and isn't handed to the observer, stays
  // permanently invisible. The Theme Editor does exactly that: every time a
  // merchant edits a setting it re-renders that section through the Section
  // Rendering API and swaps in fresh nodes — so an unwatched section reads as
  // "editing the text deleted my whole section". Hence one shared observer plus
  // `initReveals(root)`, re-run against each re-rendered section below.
  var revealObserver = null;

  function getRevealObserver() {
    if (revealObserver) return revealObserver;
    revealObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (!entry.isIntersecting) return;
        var delay = entry.target.dataset.revealDelay || '0';
        entry.target.style.transitionDelay = delay + 'ms';
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -50px 0px' });
    return revealObserver;
  }

  function initReveals(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var els = scope.querySelectorAll('.faith-reveal:not(.is-visible)');
    if (!els.length) return;

    // Reveal outright — no scroll choreography — when we can't observe, or in
    // the Theme Editor, where a section can be re-rendered while it sits
    // outside the preview viewport and would otherwise sit there blank.
    if (inDesignMode || !('IntersectionObserver' in window)) {
      els.forEach(function(el) { el.classList.add('is-visible'); });
      return;
    }

    var obs = getRevealObserver();
    els.forEach(function(el) { obs.observe(el); });
  }

  // ── Header offset + scroll state ──
  // The announcement bar and header are sibling fixed sections — measure the
  // announcement bar's actual height so the header can sit below it cleanly,
  // and toggle the scrolled state on the header after 40px of scroll.
  // The header/announcement nodes are re-created whenever the Theme Editor
  // re-renders those sections, so every read re-queries rather than closing
  // over a node that may already be detached.
  function syncOffsets() {
    var announce = document.querySelector('.shopify-section.announcement-bar-section');
    var header   = document.querySelector('.shopify-section.section-header');
    var ah = announce ? announce.offsetHeight : 0;
    document.documentElement.style.setProperty('--faith-announce-h', ah + 'px');
    var hh = ah + (header ? header.offsetHeight : 0);
    document.documentElement.style.setProperty('--faith-header-total-h', hh + 'px');
  }

  function initHeader() {
    syncOffsets();
    window.addEventListener('resize', syncOffsets, { passive: true });

    // Driven off the class itself rather than a cached flag, so a freshly
    // re-rendered header picks up the correct state on the next scroll.
    window.addEventListener('scroll', function() {
      var header = document.querySelector('.shopify-section.section-header');
      if (!header) return;
      header.classList.toggle('is-scrolled', window.scrollY > 40);
    }, { passive: true });
  }

  function init() {
    initSplash();
    initReveals(document);
    initHeader();

    // Theme Editor: re-render events hand us brand-new markup. Re-arm the
    // reveals for it (and re-measure the header) or the merchant watches the
    // section they just edited go blank. `shopify:section:load` fires on the
    // section element and bubbles; the others cover reorder/select.
    document.addEventListener('shopify:section:load', function(e) {
      initReveals(e.target);
      syncOffsets();
    });
    document.addEventListener('shopify:section:unload', syncOffsets);
    document.addEventListener('shopify:section:reorder', function() {
      initReveals(document);
    });
    document.addEventListener('shopify:section:select', function(e) {
      initReveals(e.target);
    });
    document.addEventListener('shopify:block:select', function(e) {
      initReveals(e.target);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
